import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { ScrollLockService } from '../../services/scroll-lock.service';

/**
 * Shared overlay surface for the app's side panels (broadcast details,
 * transcript editors) and centered dialogs (Voxtral schedule, upload).
 *
 * Contract (consumed by details + schedule-dialog restructures):
 * - Visibility is parent-controlled via *ngIf; the panel locks body scroll in
 *   ngOnInit and unlocks in ngOnDestroy, so every teardown path (backdrop,
 *   ESC, ×, parent force-close such as the delete-tombstone) releases the lock.
 * - Below 768px both modes become a full-screen takeover (100% × 100dvh).
 * - ESC is topmost-wins across stacked panels (static instance stack) and
 *   yields to any open clr-modal (.modal-backdrop guard) — the modal's own
 *   ESC handling must win.
 * - z-index var(--app-z-overlay)=1000: above all page content, below
 *   Clarity's nav drawer (1038) and .modal (1050) so modals opened from
 *   inside a panel stack above it.
 */
@Component({
  selector: 'app-overlay-panel',
  standalone: true,
  imports: [CommonModule, A11yModule],
  templateUrl: './overlay-panel.component.html',
  styleUrl: './overlay-panel.component.scss',
  host: {
    '[class.op-mode-side]': "mode() === 'side'",
    '[class.op-mode-center]': "mode() === 'center'",
  },
})
export class OverlayPanelComponent implements OnInit, OnDestroy {
  private static stack: OverlayPanelComponent[] = [];

  mode = input<'side' | 'center'>('side');
  size = input<'sm' | 'md' | 'lg' | 'xl'>('lg');
  ariaLabel = input.required<string>();
  closeOnBackdrop = input(true);
  closeOnEscape = input(true);
  showCloseButton = input(true);
  /** While true, backdrop click / ESC / × are inert (e.g. upload in flight). */
  busy = input(false);
  closed = output<void>();

  constructor(private scrollLock: ScrollLockService) {}

  ngOnInit(): void {
    this.scrollLock.lock();
    OverlayPanelComponent.stack.push(this);
  }

  ngOnDestroy(): void {
    this.scrollLock.unlock();
    OverlayPanelComponent.stack = OverlayPanelComponent.stack.filter(p => p !== this);
  }

  onBackdropClick(): void {
    if (this.closeOnBackdrop() && !this.busy()) {
      this.closed.emit();
    }
  }

  requestClose(): void {
    if (!this.busy()) {
      this.closed.emit();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.closeOnEscape() || this.busy()) return;
    // Only the topmost panel reacts.
    if (OverlayPanelComponent.stack.at(-1) !== this) return;
    // A Clarity modal stacked above the panel owns ESC.
    if (document.querySelector('.modal-backdrop')) return;
    this.closed.emit();
  }
}
